import path from 'node:path';
import { packageManagerEnv } from '../utils/spawn-env.js';
import fs from 'node:fs';
import crypto from 'node:crypto';
import os from 'node:os';
import net from 'node:net';
import readline from 'node:readline';
import { spawn, type ChildProcess, type SpawnOptions } from 'node:child_process';
import { fileURLToPath } from 'node:url';
import {
  FIRST_PARTY_PROVIDER_PACKAGES,
  renderAgentBuilderSupportFiles,
  type PackageManager,
} from '@standardagents/agentbuilder-template';
import { logger } from '../utils/logger.js';
import {
  loadBootstrapState,
  persistBootstrapState,
  type BootstrapSessionState,
} from '../utils/bootstrap-state.js';
import {
  cancelCliAuthSession,
  openBrowser,
  resolvePlatformAuthBrowserUrl,
  startCliAuthSession,
  waitForCliAuthCompletion,
} from '../utils/cli-auth-api.js';
import { scaffold } from './scaffold.js';
import {
  collectLocalEnvWithInk,
  getInitStepInfo,
  promptProjectNameWithInk,
  renderInitCompleteFrame,
  renderInitStepFrame,
  selectInitAuthModeWithInk,
  selectPackageManagerWithInk,
  shouldUseSideBySideAuthCards as shouldUseSideBySideAuthCardsInk,
  type InitAuthMode,
  type InitProgressStage,
} from '../tui/init-ui.js';

export interface InitOptions {
  yes?: boolean;
  local?: boolean;
  cli?: boolean;
  returnToHomeOnCancel?: boolean; // internal: used when init is launched from the home TUI
  template?: string;
  bootstrap?: string;
  apiUrl?: string;
  endpoint?: string;
  packageManager?: string;
  workspace?: boolean;
  dev?: boolean; // --no-dev sets this false: clone + configure without starting the dev server
}

interface PlatformCliStartResponse {
  session_id: string;
  auth_url: string;
  ws_url: string;
  status_url?: string;
  expires_at: number;
}

interface PlatformProjectPayload {
  id: string;
  name: string;
  repo_clone_url: string;
  repo_html_url: string | null;
  git_extra_header?: string | null;
  default_branch: string;
  api_key: string;
  package_manager: string | null;
}

interface PlatformAuthMessage {
  type: string;
  project?: PlatformProjectPayload;
  command?: string;
  bootstrap_code?: string;
  progress?: PlatformAuthProgress | null;
}

/**
 * A WebSocket TRANSPORT failure while waiting for onboarding (no runtime
 * support, handshake error, unexpected close). Distinguished from terminal
 * onboarding outcomes (expired/cancelled/legacy) so only transport failures
 * fall back to bounded HTTP status polling.
 */
export class PlatformAuthWsTransportError extends Error {}

export interface PlatformAuthProgress {
  status: string;
  message: string;
  updated_at: number;
}

interface PlatformAuthStatusResponse {
  status: 'pending' | 'completed' | 'expired' | 'cancelled';
  type?: 'project' | 'bootstrap';
  project?: PlatformProjectPayload;
  command?: string;
  bootstrap_code?: string;
  progress?: PlatformAuthProgress | null;
}

interface BootstrapConsumeSuccess {
  session_cookie: string;
  user: {
    id: string;
    email: string;
    role: string;
  };
  account: {
    id: string;
    slug?: string;
    tier?: string;
  };
  /** Present when the code was minted from a project's dashboard. */
  project?: {
    id: string;
  };
}

const DEFAULT_PLATFORM_ENDPOINT = 'https://api.standardagents.ai';
// Onboarding page that mints a fresh single-use bootstrap code.
const DEFAULT_ONBOARDING_URL = 'https://platform.standardagents.ai/projects/new';
// Known production platform origins the CLI can turn into a fresh-code page.
const KNOWN_PLATFORM_ONBOARDING_URLS: Record<string, string> = {
  'https://api.standardagents.ai': DEFAULT_ONBOARDING_URL,
  'https://platform.standardagents.ai': DEFAULT_ONBOARDING_URL,
};
const INIT_TUI_ENV_VAR = 'STANDARDAGENTS_INIT_TUI';
const GENERATED_STANDARD_AGENTS_PACKAGES = [
  '@standardagents/builder',
  '@standardagents/spec',
  ...FIRST_PARTY_PROVIDER_PACKAGES,
] as const;
const PACKAGE_MANAGERS = ['npm', 'pnpm', 'yarn', 'bun'] as const;
type SupportedPackageManager = typeof PACKAGE_MANAGERS[number];
const DEFAULT_PLATFORM_GIT_HELPER_COMMAND = '!npx -y @standardagents/cli@latest git-credential';
const MISSING_PLATFORM_PROJECT_NAME_MESSAGE =
  'The platform did not return a project name. Please run the init command again to start a new project setup.';
const LEGACY_BOOTSTRAP_RESPONSE_MESSAGE =
  'The platform returned an older onboarding response. Please run the init command again to start a new project setup.';
export const INIT_WIZARD_RETURN_TO_HOME_EXIT_CODE = 90;

interface EnsureAuthResult {
  connected: boolean;
  mode: InitAuthMode;
}

interface InitStepHeaderOptions {
  tuiMode?: boolean;
  projectName?: string;
  packageManager?: string;
  authMode?: InitAuthMode;
}

class InitWizardReturnToHomeError extends Error {
  constructor() {
    super('Return to home');
    this.name = 'InitWizardReturnToHomeError';
  }
}

export function isInitWizardReturnToHomeError(error: unknown): error is InitWizardReturnToHomeError {
  return error instanceof InitWizardReturnToHomeError;
}

function exitOrThrowReturnToHome(options: Pick<InitOptions, 'returnToHomeOnCancel'> = {}): never {
  if (options.returnToHomeOnCancel) {
    throw new InitWizardReturnToHomeError();
  }
  process.exit(INIT_WIZARD_RETURN_TO_HOME_EXIT_CODE);
}

async function prompt(question: string): Promise<string> {
  if (process.stdin.isTTY && process.stdin.isRaw) {
    process.stdin.setRawMode(false);
  }
  process.stdin.resume();

  const rl = readline.createInterface({
    input: process.stdin,
    output: process.stdout,
  });

  return new Promise((resolve) => {
    let settled = false;
    const finalize = (answer: string) => {
      if (settled) return;
      settled = true;
      resolve(answer.trim());
    };

    rl.on('SIGINT', () => {
      process.stdout.write('\n');
      process.exit(130);
    });

    rl.once('close', () => {
      if (!settled) {
        finalize('');
      }
    });

    rl.question(question, (answer) => {
      finalize(answer);
      rl.close();
    });
  });
}

export function parseBootstrapCodeInput(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';

  const flagMatch = trimmed.match(/--bootstrap(?:=|\s+)(?:"([^"]+)"|'([^']+)'|([^\s]+))/);
  if (flagMatch) {
    return (flagMatch[1] || flagMatch[2] || flagMatch[3] || '').trim();
  }

  return trimmed;
}

export function shouldSkipBrowserOpen(value?: string | null): boolean {
  return /^(none|false|0|off)$/i.test(value?.trim() ?? '');
}

function openUrl(url: string): void {
  const preferredBrowser = process.env.STANDARD_AGENTS_BROWSER?.trim() || process.env.BROWSER?.trim();
  if (shouldSkipBrowserOpen(preferredBrowser)) {
    return;
  }

  const command = process.platform === 'darwin'
    ? 'open'
    : process.platform === 'win32'
      ? 'cmd'
      : 'xdg-open';
  const args = process.platform === 'darwin' && preferredBrowser
    ? ['-a', preferredBrowser, url]
    : process.platform === 'win32'
      ? ['/c', 'start', '', url]
      : [url];
  const child = spawn(command, args, { stdio: 'ignore', detached: true });
  child.on('error', () => {
    // Browser handoff is best-effort; onboarding continues in the terminal.
  });
  child.unref();
}

async function promptPassword(question: string): Promise<string> {
  return new Promise((resolve) => {
    const stdin = process.stdin;
    const stdout = process.stdout;

    stdout.write(question);

    const wasRaw = stdin.isRaw;
    if (stdin.isTTY) {
      stdin.setRawMode(true);
    }
    stdin.resume();
    stdin.setEncoding('utf8');

    let password = '';
    let settled = false;

    const cleanup = () => {
      if (stdin.isTTY) {
        stdin.setRawMode(wasRaw ?? false);
      }
      stdin.pause();
      stdin.removeListener('data', onData);
      stdin.removeListener('end', onEndOrClose);
      stdin.removeListener('close', onEndOrClose);
    };

    const finalize = (value: string) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(value);
    };

    const onEndOrClose = () => {
      stdout.write('\n');
      finalize(password);
    };

    const onData = (char: string) => {
      const c = char.toString();

      switch (c) {
        case '\n':
        case '\r':
        case '\u0004': // Ctrl+D
          stdout.write('\n');
          finalize(password);
          break;
        case '\u0003': // Ctrl+C
          stdout.write('\n');
          process.exit(1);
          break;
        case '\u007F': // Backspace
        case '\b':
          if (password.length > 0) {
            password = password.slice(0, -1);
            stdout.clearLine(0);
            stdout.cursorTo(0);
            stdout.write(question + '*'.repeat(password.length));
          }
          break;
        default:
          password += c;
          stdout.write('*');
          break;
      }
    };

    stdin.on('data', onData);
    stdin.once('end', onEndOrClose);
    stdin.once('close', onEndOrClose);
  });
}

interface RunCommandOptions {
  quiet?: boolean;
}

const SPINNER_FRAMES = ['-', '\\', '|', '/'] as const;

async function withTuiSpinner<T>(
  enabled: boolean,
  label: string,
  operation: () => Promise<T>,
): Promise<T> {
  if (!enabled || !process.stdout.isTTY) {
    return operation();
  }

  let index = 0;
  process.stdout.write('\n');
  const renderFrame = (prefix: string) => {
    process.stdout.write(`\r${prefix} ${label}`);
  };

  renderFrame(SPINNER_FRAMES[index]);
  const timer = setInterval(() => {
    index = (index + 1) % SPINNER_FRAMES.length;
    renderFrame(SPINNER_FRAMES[index]);
  }, 80);

  try {
    const result = await operation();
    clearInterval(timer);
    renderFrame('✓');
    process.stdout.write('\n');
    return result;
  } catch (error) {
    clearInterval(timer);
    renderFrame('!');
    process.stdout.write('\n');
    throw error;
  }
}

function appendOutputTail(existing: string, chunk: string, maxLength = 8000): string {
  const combined = existing + chunk;
  if (combined.length <= maxLength) return combined;
  return combined.slice(combined.length - maxLength);
}

function runCommand(command: string, args: string[], cwd: string, options: RunCommandOptions = {}): Promise<void> {
  return new Promise((resolve, reject) => {
    const quiet = options.quiet ?? false;
    const child = spawn(command, args, {
      cwd,
      stdio: quiet ? ['ignore', 'pipe', 'pipe'] : 'inherit',
      shell: false,
      // npx-inherited npm_config_allow_scripts fatals npm 12 project installs
      // (EALLOWSCRIPTS); approvals live in the generated package.json instead.
      env: packageManagerEnv(),
    });

    let outputTail = '';
    if (quiet) {
      child.stdout?.on('data', (chunk: Buffer | string) => {
        outputTail = appendOutputTail(outputTail, chunk.toString());
      });
      child.stderr?.on('data', (chunk: Buffer | string) => {
        outputTail = appendOutputTail(outputTail, chunk.toString());
      });
    }

    child.on('close', (code) => {
      if (code === 0) {
        resolve();
      } else {
        const details = outputTail.trim();
        if (details) {
          reject(new Error(`Command failed with exit code ${code}\n${details}`));
          return;
        }
        reject(new Error(`Command failed with exit code ${code}`));
      }
    });

    child.on('error', reject);
  });
}

export function devServerSpawnOptions(cwd: string): SpawnOptions {
  return {
    cwd,
    stdio: 'inherit',
    shell: false,
  };
}

function startForegroundDevServer(command: string, args: string[], cwd: string): ChildProcess {
  return spawn(command, args, devServerSpawnOptions(cwd));
}

function waitForDevServerExit(child: ChildProcess): Promise<number | null> {
  return new Promise((resolve, reject) => {
    child.once('error', reject);
    child.once('close', (code) => resolve(code));
  });
}

export function devCommandHint(packageManager: SupportedPackageManager): string {
  if (packageManager === 'npm') return 'npm run dev';
  if (packageManager === 'bun') return 'bun run dev';
  return `${packageManager} dev`;
}

function canConnectToPort(port: number): Promise<boolean> {
  return new Promise((resolve) => {
    const socket = net.createConnection({ host: '127.0.0.1', port });
    socket.once('connect', () => {
      socket.destroy();
      resolve(true);
    });
    socket.once('error', () => resolve(false));
    socket.setTimeout(500, () => {
      socket.destroy();
      resolve(false);
    });
  });
}

async function findAvailablePort(startPort: number): Promise<number> {
  for (let port = startPort; port < startPort + 100; port += 1) {
    if (!(await canConnectToPort(port))) return port;
  }
  throw new Error(`No available local dev port found near ${startPort}`);
}

export function buildDevServerCommand(
  packageManager: SupportedPackageManager,
  port: number,
): string[] {
  const viteArgs = ['--host', '127.0.0.1', '--port', String(port), '--strictPort'];
  return packageManager === 'npm'
    ? ['run', 'dev', '--', ...viteArgs]
    : ['dev', ...viteArgs];
}

export async function waitForLocalDevServer(url: string, timeoutMs = 60_000): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    try {
      const response = await fetch(url, { cache: 'no-store' });
      if (response.ok) return true;
    } catch {
      // The dev server is still starting.
    }
    await sleep(500);
  }
  return false;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function toKebabCase(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')  // Replace non-alphanumeric with hyphens
    .replace(/^-+|-+$/g, '');      // Remove leading/trailing hyphens
}

export function normalizeProjectName(raw: string): string {
  return toKebabCase(raw.trim());
}

function isNonEmptyDirectory(targetPath: string): boolean {
  if (!fs.existsSync(targetPath)) return false;
  const stat = fs.statSync(targetPath);
  if (!stat.isDirectory()) return false;
  return fs.readdirSync(targetPath).length > 0;
}

function hasBuilderDependency(targetPath: string): boolean {
  const packageJsonPath = path.join(targetPath, 'package.json');
  if (!fs.existsSync(packageJsonPath)) return false;

  try {
    const pkg = JSON.parse(fs.readFileSync(packageJsonPath, 'utf-8')) as Record<string, any>;
    const deps = { ...(pkg.dependencies ?? {}), ...(pkg.devDependencies ?? {}) };
    return typeof deps === 'object' && Object.prototype.hasOwnProperty.call(deps, '@standardagents/builder');
  } catch {
    return false;
  }
}

export function isStandardAgentsProjectDir(targetPath: string): boolean {
  const agentsDir = path.join(targetPath, 'agents');
  if (!fs.existsSync(agentsDir) || !fs.statSync(agentsDir).isDirectory()) {
    return false;
  }
  if (hasBuilderDependency(targetPath)) return true;

  return (
    fs.existsSync(path.join(targetPath, 'vite.config.ts')) &&
    fs.existsSync(path.join(targetPath, 'tsconfig.json'))
  );
}

interface ProjectTarget {
  projectName: string;
  projectPath: string;
  existingBootstrapState: BootstrapSessionState | null;
  resumeBootstrap: boolean;
  useExistingProject: boolean;
}

export async function resolveProjectTarget(
  cwd: string,
  initialProjectName: string | undefined,
  options: InitOptions,
  promptProjectName: (question: string) => Promise<string> = prompt,
): Promise<ProjectTarget> {
  if (!initialProjectName && isStandardAgentsProjectDir(cwd)) {
    const dirName = path.basename(cwd) || 'standard-agents';
    const projectName = normalizeProjectName(dirName) || dirName;
    const existingBootstrapState = loadBootstrapState(cwd);
    return {
      projectName,
      projectPath: cwd,
      existingBootstrapState,
      resumeBootstrap: existingBootstrapState !== null,
      useExistingProject: true,
    };
  }
  let candidate = initialProjectName?.trim() ?? '';
  let hasPromptedOnce = false;

  while (true) {
    if (!candidate) {
      if (options.yes) {
        throw new Error('Project name is required');
      }
      candidate = (await promptProjectName(hasPromptedOnce ? 'Project name (try another): ' : 'Project name: ')).trim();
      hasPromptedOnce = true;
      continue;
    }

    const projectName = normalizeProjectName(candidate);
    if (!projectName) {
      if (options.yes) {
        throw new Error('Project name must include at least one letter or number');
      }
      logger.warning(`Project name "${candidate}" is invalid. Use letters and numbers.`);
      candidate = '';
      continue;
    }

    if (projectName !== candidate) {
      logger.info(`Using project directory "${projectName}"`);
    }

    const projectPath = path.join(cwd, projectName);
    const exists = fs.existsSync(projectPath);
    if (exists && !fs.statSync(projectPath).isDirectory()) {
      if (options.yes) {
        throw new Error(`Path "${projectName}" already exists and is not a directory`);
      }
      logger.warning(`Path "${projectName}" already exists and is not a directory.`);
      candidate = '';
      continue;
    }

    const existingBootstrapState = loadBootstrapState(projectPath);
    const resumeBootstrap = exists && existingBootstrapState !== null;
    const existingStandardAgentsProject = exists && isStandardAgentsProjectDir(projectPath);
    if (!resumeBootstrap && !existingStandardAgentsProject && isNonEmptyDirectory(projectPath)) {
      if (options.yes) {
        throw new Error(`Directory "${projectName}" already exists and is not empty`);
      }
      logger.warning(`Directory "${projectName}" already exists and is not empty.`);
      candidate = '';
      continue;
    }

    return {
      projectName,
      projectPath,
      existingBootstrapState,
      resumeBootstrap,
      useExistingProject: resumeBootstrap || existingStandardAgentsProject,
    };
  }
}

function detectPackageManager(): SupportedPackageManager {
  // Check environment variable first (most reliable when running via npx/pnpm exec)
  const userAgent = process.env.npm_config_user_agent;
  if (userAgent) {
    if (userAgent.includes('pnpm')) return 'pnpm';
    if (userAgent.includes('yarn')) return 'yarn';
    if (userAgent.includes('bun')) return 'bun';
  }

  // Check for lockfiles
  if (fs.existsSync('pnpm-lock.yaml')) return 'pnpm';
  if (fs.existsSync('yarn.lock')) return 'yarn';
  if (fs.existsSync('bun.lockb')) return 'bun';
  if (fs.existsSync('package-lock.json')) return 'npm';

  return 'npm';
}

export function resolvePackageManager(
  requested: string | undefined,
  detected: SupportedPackageManager,
): SupportedPackageManager {
  if (!requested) return detected;
  const normalized = requested.trim();
  if ((PACKAGE_MANAGERS as readonly string[]).includes(normalized)) {
    return normalized as SupportedPackageManager;
  }
  throw new Error('package-manager must be npm, pnpm, yarn, or bun');
}

export function isInitTuiEnabled(options: Pick<InitOptions, 'yes' | 'cli'> = {}): boolean {
  if (options.yes) return false;
  if (options.cli) return false;
  if (!process.stdin.isTTY || !process.stdout.isTTY) return false;
  if (process.env[INIT_TUI_ENV_VAR] === '0') return false;
  return true;
}

function clearTerminalScreen(stdout: NodeJS.WriteStream = process.stdout): void {
  stdout.write('\x1b[2J\x1b[H');
}

function silenceLoggerOutput(enabled: boolean): () => void {
  if (!enabled) return () => {};

  const original = {
    success: logger.success,
    warning: logger.warning,
    info: logger.info,
    log: logger.log,
  };

  logger.success = () => {};
  logger.warning = () => {};
  logger.info = () => {};
  logger.log = () => {};

  return () => {
    logger.success = original.success;
    logger.warning = original.warning;
    logger.info = original.info;
    logger.log = original.log;
  };
}

function renderInitStepHeader(
  stage: InitProgressStage,
  title: string,
  subtitle?: string,
  options: InitStepHeaderOptions = {},
): void {
  const mode = options.authMode ?? 'standardagents';
  const { step, total, labels } = getInitStepInfo(stage, mode);
  const progress = labels.map((label, index) => {
    const markerIndex = index + 1;
    if (markerIndex < step) return `✓ ${label}`;
    if (markerIndex === step) return `→ ${label}`;
    return `· ${label}`;
  }).join('  ');

  if (options.tuiMode) {
    clearTerminalScreen();
    process.stdout.write(
      renderInitStepFrame({
        stage,
        title,
        subtitle,
        projectName: options.projectName,
        packageManager: options.packageManager,
        authMode: options.authMode,
      }),
    );
    return;
  }

  logger.log('');
  logger.log(progress);
  logger.info(`Step ${step}/${total}: ${title}`);
  if (subtitle) {
    logger.log(subtitle);
  }
  logger.log('');
}

export function shouldUseSideBySideAuthCards(columns: number): boolean {
  return shouldUseSideBySideAuthCardsInk(columns);
}

async function selectInitAuthMode(context: {
  projectName?: string;
  packageManager?: string;
} = {}): Promise<InitAuthMode> {
  if (!process.stdin.isTTY || !process.stdout.isTTY) {
    const answer = (await prompt('Setup mode: [S]tandardAgents or [L]ocal? (default: StandardAgents): ')).toLowerCase();
    if (answer === 'l' || answer === 'local') return 'local';
    return 'standardagents';
  }

  const selection = await selectInitAuthModeWithInk(context);
  if (!selection) {
    throw new InitWizardReturnToHomeError();
  }
  return selection;
}

async function ensurePlatformAuth(
  options: InitOptions,
  quietLogs = false,
  context: {
    projectName?: string;
    packageManager?: string;
  } = {},
): Promise<EnsureAuthResult> {
  const logInfo = (message: string) => {
    if (!quietLogs) logger.info(message);
  };
  const logWarning = (message: string) => {
    if (!quietLogs) logger.warning(message);
  };
  const logSuccess = (message: string) => {
    if (!quietLogs) logger.success(message);
  };
  const logLine = (message = '') => {
    if (!quietLogs) logger.log(message);
  };

  if (options.bootstrap?.trim()) {
    logInfo('Bootstrap code provided. Project will be connected during setup.');
    return { connected: true, mode: 'standardagents' };
  }

  if (options.yes) {
    logInfo('Skipping platform auth in non-interactive mode. Run "agents login --bootstrap <code>" later to connect.');
    return { connected: false, mode: 'local' };
  }

  const authMode = await selectInitAuthMode(context);
  if (authMode === 'local') {
    logInfo('Continuing in local-only mode. Run "agents login --bootstrap <code>" anytime to connect.');
    return { connected: false, mode: 'local' };
  }

  const endpoint = resolvePlatformEndpoint(options);
  let authAppUrl = resolvePlatformAuthBrowserUrl(endpoint);
  let sessionId: string | null = null;

  logLine('');
  logInfo('Starting StandardAgents account setup...');
  logLine(`Platform API: ${endpoint}`);

  try {
    const started = await startCliAuthSession(endpoint);
    sessionId = started.session_id;
    authAppUrl = started.auth_url;

    logLine(`Auth app: ${authAppUrl}`);
    logLine('');
    logLine('Complete login/signup in your browser. This window is waiting for realtime confirmation...');

    if (process.stdin.isTTY) {
      const opened = openBrowser(authAppUrl);
      if (opened) {
        logInfo('Opened browser for authentication.');
      } else {
        logWarning('Could not automatically open browser. Open the URL above manually.');
      }
    }

    try {
      const completion = await waitForCliAuthCompletion(started.ws_url, started.expires_at);
      options.bootstrap = completion.bootstrapCode;
      logSuccess('Authentication received from browser.');
      return { connected: true, mode: 'standardagents' };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Realtime auth handoff failed';
      logWarning(`${message} Switching to manual bootstrap command entry.`);
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to start realtime auth session';
    logWarning(`${message} Switching to manual bootstrap command entry.`);
    logLine(`Auth app: ${authAppUrl}`);
  }

  logLine('');
  const pasted = await prompt('Paste bootstrap code or full command (Enter to continue local-only): ');
  const bootstrapCode = parseBootstrapCodeInput(pasted);
  if (!bootstrapCode) {
    if (sessionId) {
      await cancelCliAuthSession(endpoint, sessionId);
    }
    logInfo('No bootstrap code provided. Continuing in local-only mode.');
    return { connected: false, mode: 'local' };
  }

  if (sessionId) {
    await cancelCliAuthSession(endpoint, sessionId);
  }
  options.bootstrap = bootstrapCode;
  logSuccess('Bootstrap code captured.');
  return { connected: true, mode: 'standardagents' };
}

// Injected at build time by tsup (see tsup.config.ts)
// During publish, CLI_PUBLISH_VERSION env var is set to the actual version being published
declare const __CLI_VERSION__: string;
declare const __SIP_VERSION__: string;

function getCliVersion(): string {
  return typeof __CLI_VERSION__ === 'undefined' ? 'latest' : __CLI_VERSION__;
}

function getSipVersion(): string {
  return typeof __SIP_VERSION__ === 'undefined' ? 'latest' : __SIP_VERSION__;
}

function shouldUseWorkspacePackages(options: Pick<InitOptions, 'workspace'>): boolean {
  return options.workspace === true || process.env.STANDARDAGENTS_WORKSPACE === '1';
}

/**
 * Resolve the package specifier used when installing @standardagents packages.
 *
 * - Workspace mode (`--workspace` or `STANDARDAGENTS_WORKSPACE=1`) uses `workspace:*`
 *   and requires pnpm.
 * - Otherwise, install the exact CLI publish version.
 */
export function resolvePackageSpecifier(
  options: Pick<InitOptions, 'workspace'>,
  packageManager: 'npm' | 'pnpm' | 'yarn' | 'bun',
): string {
  if (!shouldUseWorkspacePackages(options)) {
    return getCliVersion();
  }

  if (packageManager !== 'pnpm') {
    throw new Error(
      'Workspace package linking requires pnpm. Re-run with pnpm or remove --workspace.',
    );
  }

  return 'workspace:*';
}

export function resolveStandardAgentsPackageSpecifier(
  packageName: string,
  packageSpecifier: string,
): string {
  if (packageSpecifier === 'workspace:*' && packageName === '@standardagents/sip') {
    return getSipVersion();
  }

  return packageSpecifier;
}

function findWorkspaceRoot(startDir: string): string | null {
  let currentDir = startDir;

  while (true) {
    if (fs.existsSync(path.join(currentDir, 'pnpm-workspace.yaml'))) {
      return currentDir;
    }

    const parentDir = path.dirname(currentDir);
    if (parentDir === currentDir) {
      return null;
    }

    currentDir = parentDir;
  }
}

export function findStandardAgentsWorkspaceRoot(
  startDir = path.dirname(fileURLToPath(import.meta.url)),
): string | null {
  const workspaceRoot = findWorkspaceRoot(startDir);
  if (!workspaceRoot) return null;

  const rootPackageJsonPath = path.join(workspaceRoot, 'package.json');
  if (!fs.existsSync(rootPackageJsonPath)) return null;

  try {
    const rootPackageJson = JSON.parse(fs.readFileSync(rootPackageJsonPath, 'utf-8')) as { name?: string };
    return rootPackageJson.name === 'standardagents' ? workspaceRoot : null;
  } catch {
    return null;
  }
}

export function resolveStandardAgentsWorkspaceRoot(startDirs: string[] = [
  path.dirname(fileURLToPath(import.meta.url)),
  process.cwd(),
]): string | null {
  const explicitRoot = process.env.STANDARDAGENTS_WORKSPACE_ROOT?.trim();
  if (explicitRoot) {
    const workspaceRoot = findStandardAgentsWorkspaceRoot(path.resolve(explicitRoot));
    if (workspaceRoot) return workspaceRoot;

    throw new Error(
      `STANDARDAGENTS_WORKSPACE_ROOT=${path.resolve(explicitRoot)} is not a Standard Agents workspace root`,
    );
  }

  for (const startDir of startDirs) {
    const workspaceRoot = findStandardAgentsWorkspaceRoot(startDir);
    if (workspaceRoot) return workspaceRoot;
  }

  return null;
}

function workspacePackageDir(workspaceRoot: string, packageName: string): string {
  return path.join(workspaceRoot, 'packages', packageName.replace('@standardagents/', ''));
}

function validateWorkspacePackageDir(packageName: string, packageDir: string): void {
  const dependencyPackageJsonPath = path.join(packageDir, 'package.json');
  if (!fs.existsSync(dependencyPackageJsonPath)) {
    throw new Error(`Could not find workspace package for ${packageName} at ${packageDir}`);
  }

  const dependencyPackageJson = JSON.parse(fs.readFileSync(dependencyPackageJsonPath, 'utf-8')) as {
    name?: string;
  };
  if (dependencyPackageJson.name !== packageName) {
    throw new Error(
      `Expected ${packageDir} to contain ${packageName}, found ${dependencyPackageJson.name ?? 'unknown package'}`,
    );
  }
}

export function rewriteStandardAgentsDependenciesForWorkspace(
  projectPath: string,
  workspaceRoot: string,
): string[] {
  const packageJsonPath = path.join(projectPath, 'package.json');
  const packageJson = JSON.parse(fs.readFileSync(packageJsonPath, 'utf-8')) as {
    dependencies?: Record<string, string>;
  };
  const dependencies = packageJson.dependencies ?? {};
  const linkedPackages: string[] = [];

  for (const packageName of GENERATED_STANDARD_AGENTS_PACKAGES) {
    if (!(packageName in dependencies)) continue;

    const packageDir = workspacePackageDir(workspaceRoot, packageName);
    validateWorkspacePackageDir(packageName, packageDir);

    dependencies[packageName] = `link:${packageDir}`;
    linkedPackages.push(packageName);
  }

  if (linkedPackages.length > 0) {
    packageJson.dependencies = dependencies;
    fs.writeFileSync(packageJsonPath, `${JSON.stringify(packageJson, null, 2)}\n`, 'utf-8');
  }

  return linkedPackages;
}

function preparePlatformProjectDependencies(
  projectPath: string,
  packageManager: SupportedPackageManager,
): string[] {
  if (process.env.STANDARDAGENTS_WORKSPACE !== '1') {
    return [];
  }
  if (packageManager !== 'pnpm') {
    throw new Error('STANDARDAGENTS_WORKSPACE=1 currently requires pnpm for local package linking');
  }

  const workspaceRoot = resolveStandardAgentsWorkspaceRoot();
  if (!workspaceRoot) {
    throw new Error('STANDARDAGENTS_WORKSPACE=1 was set, but no Standard Agents workspace root was found');
  }

  return rewriteStandardAgentsDependenciesForWorkspace(projectPath, workspaceRoot);
}

function shellQuote(value: string): string {
  return `"${value.replace(/(["\\$`])/g, '\\$1')}"`
}

export function findWorkspaceCommandRoot(projectPath: string): string | null {
  let current = path.dirname(projectPath);

  while (true) {
    const packageJsonPath = path.join(current, 'package.json');
    if (fs.existsSync(packageJsonPath)) {
      try {
        const packageJson = JSON.parse(fs.readFileSync(packageJsonPath, 'utf-8'));
        if (packageJson?.scripts && typeof packageJson.scripts['sandbox:hmr'] === 'string') {
          return current;
        }
      } catch {
        // Ignore invalid package.json files while walking upward.
      }
    }

    const parent = path.dirname(current);
    if (parent === current) {
      return null;
    }
    current = parent;
  }
}

export function configureWorkspaceProjectScripts(projectPath: string, projectName: string): boolean {
  const packageJsonPath = path.join(projectPath, 'package.json');
  if (!fs.existsSync(packageJsonPath)) return false;

  const workspaceRoot = findWorkspaceCommandRoot(projectPath);
  if (!workspaceRoot) return false;

  const workspacePackageJsonPath = path.join(workspaceRoot, 'package.json');
  const workspacePackageJson = JSON.parse(fs.readFileSync(workspacePackageJsonPath, 'utf-8'));
  const packageJson = JSON.parse(fs.readFileSync(packageJsonPath, 'utf-8'));
  const scripts =
    packageJson.scripts && typeof packageJson.scripts === 'object'
      ? packageJson.scripts
      : {};

  const relativeRoot = path.relative(projectPath, workspaceRoot) || '.';
  scripts['sandbox:hmr'] = `pnpm -C ${shellQuote(relativeRoot)} sandbox:hmr -- ${shellQuote(projectName)}`;
  if (workspacePackageJson?.scripts && typeof workspacePackageJson.scripts['cli:login:workspace'] === 'string') {
    scripts['cli:login:workspace'] = `pnpm -C ${shellQuote(relativeRoot)} cli:login:workspace -- ${shellQuote(projectName)}`;
  }
  packageJson.scripts = scripts;

  fs.writeFileSync(packageJsonPath, JSON.stringify(packageJson, null, 2) + '\n', 'utf-8');
  return true;
}

export function resolvePostInitRunCommand(
  packageManager: 'npm' | 'pnpm' | 'yarn' | 'bun',
  projectPath: string,
  workspacePackages: boolean,
): string {
  if (workspacePackages) {
    const packageJsonPath = path.join(projectPath, 'package.json');
    if (fs.existsSync(packageJsonPath)) {
      try {
        const packageJson = JSON.parse(fs.readFileSync(packageJsonPath, 'utf-8'));
        if (packageJson?.scripts && typeof packageJson.scripts['sandbox:hmr'] === 'string') {
          return packageManager === 'npm' ? 'npm run sandbox:hmr' : `${packageManager} sandbox:hmr`;
        }
      } catch {
        // Fall through to the standard dev command below.
      }
    }
  }

  return packageManager === 'npm' ? 'npm run dev' : `${packageManager} dev`;
}

async function selectPackageManager(
  detected: 'npm' | 'pnpm' | 'yarn' | 'bun',
  context: {
    projectName?: string;
    authMode?: InitAuthMode;
  } = {},
): Promise<'npm' | 'pnpm' | 'yarn' | 'bun'> {
  if (!process.stdin.isTTY || !process.stdout.isTTY) return detected;
  const selection = await selectPackageManagerWithInk(detected, context);
  if (!selection) {
    throw new InitWizardReturnToHomeError();
  }
  return selection;
}

export function resolvePlatformEndpoint(options: Pick<InitOptions, 'apiUrl' | 'endpoint'>): string {
  return options.endpoint || options.apiUrl || process.env.PLATFORM_ENDPOINT || DEFAULT_PLATFORM_ENDPOINT;
}

function isBootstrapConsumeSuccess(body: unknown): body is BootstrapConsumeSuccess {
  const candidate = body as BootstrapConsumeSuccess;
  return !!(
    candidate
    && candidate.user
    && typeof candidate.user.id === 'string'
    && typeof candidate.user.email === 'string'
    && typeof candidate.user.role === 'string'
    && candidate.account
    && typeof candidate.account.id === 'string'
  );
}

// Derive the fresh-code onboarding page from the resolved endpoint when it is a
// known production origin, otherwise point at the canonical onboarding page.
function onboardingUrlForEndpoint(endpoint?: string): string {
  if (!endpoint) return DEFAULT_ONBOARDING_URL;
  try {
    const origin = new URL(endpoint).origin;
    return KNOWN_PLATFORM_ONBOARDING_URLS[origin] ?? DEFAULT_ONBOARDING_URL;
  } catch {
    return DEFAULT_ONBOARDING_URL;
  }
}

// Bootstrap-code rejections from the platform ("Invalid bootstrap code",
// "Bootstrap code expired", "Bootstrap code already used", "Bootstrap code
// revoked") all mention "bootstrap code"; codes are single-use and short-lived.
function isBootstrapCodeFailureMessage(message: string): boolean {
  return /bootstrap code/i.test(message);
}

function formatBootstrapExchangeError(status: number, body: unknown, endpoint?: string): string {
  const payload = body as { error?: { message?: string }; message?: string };
  const message = payload?.error?.message || payload?.message;
  const base = message
    ? `Bootstrap exchange failed (${status}): ${message}`
    : `Bootstrap exchange failed (${status})`;
  if (message && isBootstrapCodeFailureMessage(message)) {
    const url = onboardingUrlForEndpoint(endpoint);
    return `${base}\nBootstrap codes are single-use and expire quickly. Grab a fresh one from ${url} and run the command again.`;
  }
  return base;
}

function extractSessionCookie(response: Response): string | null {
  const headersWithSetCookie = response.headers as Headers & {
    getSetCookie?: () => string[];
  };

  const extractFromLine = (value: string): string | null => {
    const trimmed = value.trim();
    if (!trimmed.toLowerCase().startsWith('session=')) return null;
    const semi = trimmed.indexOf(';');
    return semi === -1 ? trimmed : trimmed.slice(0, semi);
  };

  if (typeof headersWithSetCookie.getSetCookie === 'function') {
    for (const line of headersWithSetCookie.getSetCookie()) {
      const extracted = extractFromLine(line);
      if (extracted) return extracted;
    }
  }

  const combined = response.headers.get('set-cookie');
  if (!combined) return null;

  const combinedMatch = combined.match(/(?:^|,\s*)(session=[^;,]+)/i);
  if (combinedMatch?.[1]) {
    return combinedMatch[1];
  }

  return extractFromLine(combined);
}

export async function exchangeBootstrapCode(
  endpoint: string,
  code: string,
  fetchImpl: typeof fetch = fetch,
): Promise<BootstrapConsumeSuccess> {
  const response = await fetchImpl(`${endpoint}/auth/bootstrap-code/consume`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });

  let body: unknown = {};
  try {
    body = await response.json();
  } catch {
    body = {};
  }

  if (!response.ok) {
    throw new Error(formatBootstrapExchangeError(response.status, body, endpoint));
  }

  if (!isBootstrapConsumeSuccess(body)) {
    throw new Error('Bootstrap exchange failed: invalid response payload');
  }

  const sessionCookie = extractSessionCookie(response);
  if (!sessionCookie) {
    throw new Error('Bootstrap exchange failed: session cookie missing in response');
  }

  const parsed = body as Omit<BootstrapConsumeSuccess, 'session_cookie'>;
  return {
    session_cookie: sessionCookie,
    user: parsed.user,
    account: parsed.account,
    ...(typeof parsed.project?.id === 'string' && parsed.project.id.trim()
      ? { project: { id: parsed.project.id.trim() } }
      : {}),
  };
}

export async function prepareBootstrapSession(
  projectPath: string,
  options: InitOptions,
  fetchImpl: typeof fetch = fetch,
): Promise<{ state: BootstrapSessionState; resumed: boolean } | null> {
  const endpoint = resolvePlatformEndpoint(options);
  const existing = loadBootstrapState(projectPath);
  if (existing && existing.endpoint === endpoint) {
    return { state: existing, resumed: true };
  }

  const bootstrapCode = options.bootstrap?.trim();
  if (!bootstrapCode) return null;

  const consumed = await exchangeBootstrapCode(endpoint, bootstrapCode, fetchImpl);
  const state: BootstrapSessionState = {
    version: 1,
    endpoint,
    authenticated_at: Date.now(),
    session_cookie: consumed.session_cookie,
    user: consumed.user,
    account: consumed.account,
  };
  persistBootstrapState(projectPath, state);
  return { state, resumed: false };
}

export function normalizeApiUrl(input?: string): string {
  return (
    input ||
    process.env.STANDARD_AGENTS_API_URL ||
    process.env.PLATFORM_ENDPOINT ||
    process.env.STANDARD_AGENTS_PLATFORM_URL ||
    DEFAULT_PLATFORM_ENDPOINT
  ).replace(/\/+$/, '');
}

export async function startPlatformCliSession(params: {
  endpoint: string;
  projectName?: string;
  packageManager: string;
}): Promise<PlatformCliStartResponse> {
  const body: Record<string, string> = {
    machine_name: os.hostname(),
    package_manager: params.packageManager,
  };
  const projectName = params.projectName?.trim();
  if (projectName) {
    body.project_name = projectName;
  }

  let response: Response;
  try {
    response = await fetch(`${params.endpoint}/cli/auth/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (error) {
    throw new Error(
      `Could not reach the Standard Agents platform at ${params.endpoint}: ${
        error instanceof Error ? error.message : String(error)
      }`,
    );
  }

  if (!response.ok) {
    const bodyText = await response.text().catch(() => '');
    throw new Error(`Platform auth failed to start (${response.status})${bodyText ? `: ${bodyText}` : ''}`);
  }

  return response.json() as Promise<PlatformCliStartResponse>;
}

export function resolvePlatformProjectDirectoryName(
  project: { name: string },
  requestedProjectName?: string,
): string {
  const platformName = project.name.trim();
  if (platformName) return platformName;

  const fallbackName = requestedProjectName?.trim();
  if (fallbackName) return fallbackName;

  throw new Error(MISSING_PLATFORM_PROJECT_NAME_MESSAGE);
}

export function resolvePlatformProjectPackageManager(
  projectPackageManager: string | null | undefined,
  requestedPackageManager: SupportedPackageManager,
): SupportedPackageManager {
  return resolvePackageManager(projectPackageManager ?? undefined, requestedPackageManager);
}

export function sanitizeGitRemoteUrl(remoteUrl: string): string {
  try {
    const url = new URL(remoteUrl);
    url.username = '';
    url.password = '';
    return url.toString();
  } catch {
    return remoteUrl.replace(/\/\/[^/@\s]+@/, '//');
  }
}

export function cloneDisplayUrl(project: PlatformProjectPayload): string {
  return project.repo_html_url || sanitizeGitRemoteUrl(project.repo_clone_url);
}

export function gitCloneArgs(project: PlatformProjectPayload, projectName: string): string[] {
  const args: string[] = [];
  if (project.git_extra_header) {
    args.push('-c', `http.extraHeader=${project.git_extra_header}`);
  }
  args.push('clone', project.repo_clone_url, projectName);
  return args;
}

export function platformGitCredentialHelperCommand(cliEntry = process.argv[1] ?? ''): string {
  if (process.env.STANDARDAGENTS_WORKSPACE === '1' && cliEntry && fs.existsSync(cliEntry)) {
    return `!node ${shellQuote(path.resolve(cliEntry))} git-credential`;
  }
  if (process.env.STANDARD_AGENTS_GIT_HELPER?.trim()) {
    return process.env.STANDARD_AGENTS_GIT_HELPER.trim();
  }
  // Pin the helper to the CLI's own published version so prerelease installs
  // (e.g. @next) keep minting credentials with the matching CLI behavior.
  const cliVersion = getCliVersion();
  if (cliVersion !== 'latest') {
    return `!npx -y @standardagents/cli@${cliVersion} git-credential`;
  }
  return DEFAULT_PLATFORM_GIT_HELPER_COMMAND;
}

async function configurePlatformGitCredentialHelper(projectPath: string): Promise<void> {
  // Reset any inherited credential helpers (notably the global macOS
  // `osxkeychain`, or `cache`) for this repo BEFORE adding ours. Otherwise the
  // platform's short-lived Artifacts token gets stored by osxkeychain on the
  // first push and shadows our minting helper on every later push; once that
  // token expires (~1h) the cached value 403s and git never re-mints it (git
  // only re-prompts credentials on HTTP 401, not 403), so pushes stay broken.
  // An empty value clears the inherited chain; we then --add our helper as the
  // sole provider so every git operation mints a fresh token from the API key.
  await runCommand('git', ['config', '--local', '--unset-all', 'credential.helper'], projectPath).catch(() => {});
  await runCommand('git', ['config', '--local', '--add', 'credential.helper', ''], projectPath);
  await runCommand('git', ['config', '--local', '--add', 'credential.helper', platformGitCredentialHelperCommand()], projectPath);
  await runCommand('git', ['config', '--local', 'credential.useHttpPath', 'true'], projectPath);
}

function isRetryablePlatformAuthStatus(status: number): boolean {
  return status === 502 || status === 503 || status === 504;
}

export async function waitForPlatformProjectStatus(
  statusUrl: string,
  expiresAt: number,
  options: {
    onProgress?: (progress: PlatformAuthProgress) => void;
    pollIntervalMs?: number;
  } = {},
): Promise<PlatformProjectPayload> {
  let lastProgressKey = '';
  while (Date.now() < expiresAt) {
    const response = await fetch(statusUrl, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      if (isRetryablePlatformAuthStatus(response.status)) {
        await sleep(options.pollIntervalMs ?? 1_500);
        continue;
      }
      throw new Error(`Platform auth status failed (${response.status})`);
    }

    const status = await response.json() as PlatformAuthStatusResponse;
    if (status.progress?.message) {
      const progressKey = `${status.progress.status}:${status.progress.message}:${status.progress.updated_at}`;
      if (progressKey !== lastProgressKey) {
        lastProgressKey = progressKey;
        options.onProgress?.(status.progress);
      }
    }
    if (status.status === 'completed' && status.type === 'project' && status.project) {
      return status.project;
    }
    if (status.status === 'completed') {
      throw new Error(LEGACY_BOOTSTRAP_RESPONSE_MESSAGE);
    }
    if (status.status === 'expired') {
      throw new Error('Onboarding session expired. Run the init command again to start a new session.');
    }
    if (status.status === 'cancelled') {
      throw new Error('Onboarding was cancelled in the browser. Run the init command again when you are ready.');
    }

    await sleep(options.pollIntervalMs ?? 1_500);
  }

  throw new Error('Timed out waiting for platform project creation');
}

export function waitForPlatformProjectWs(
  wsUrl: string,
  expiresAt: number,
  options: { onProgress?: (progress: PlatformAuthProgress) => void } = {},
): Promise<PlatformProjectPayload> {
  return new Promise((resolve, reject) => {
    const WebSocketCtor = globalThis.WebSocket;
    if (!WebSocketCtor) {
      reject(new PlatformAuthWsTransportError('This Node.js runtime does not provide WebSocket support'));
      return;
    }

    let socket: WebSocket;
    try {
      socket = new WebSocketCtor(wsUrl);
    } catch (error) {
      reject(new PlatformAuthWsTransportError(
        `Platform auth websocket could not open: ${error instanceof Error ? error.message : String(error)}`,
      ));
      return;
    }
    let settled = false;
    let lastProgressKey = '';
    const timeout = setTimeout(() => {
      settled = true;
      socket.close();
      reject(new Error('Timed out waiting for platform project creation'));
    }, Math.max(1_000, expiresAt - Date.now()));

    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      socket.close();
      fn();
    };

    socket.addEventListener('open', () => {
      try {
        socket.send(JSON.stringify({ type: 'ready' }));
      } catch {
        settle(() => reject(new PlatformAuthWsTransportError('Platform auth websocket could not send ready')));
      }
    });

    socket.addEventListener('message', (event) => {
      if (typeof event.data !== 'string') {
        settle(() => reject(new PlatformAuthWsTransportError('Platform auth websocket sent a non-text frame')));
        return;
      }
      let message: PlatformAuthMessage;
      try {
        message = JSON.parse(event.data) as PlatformAuthMessage;
      } catch {
        settle(() => reject(new PlatformAuthWsTransportError('Platform auth websocket sent invalid JSON')));
        return;
      }
      // Real progress transitions pushed by the platform (PL-01) drive the
      // same terminal display the HTTP status loop fed.
      if (message.type === 'progress' && message.progress?.message) {
        const progress = message.progress;
        const progressKey = `${progress.status}:${progress.message}:${progress.updated_at}`;
        if (progressKey !== lastProgressKey) {
          lastProgressKey = progressKey;
          options.onProgress?.(progress);
        }
        return;
      }
      const project = message.project;
      if (message.type === 'authenticated' && project) {
        settle(() => resolve(project));
      } else if (message.type === 'expired' || message.type === 'cancelled') {
        settle(() => reject(new Error(
          message.type === 'expired'
            ? 'Onboarding session expired. Run the init command again to start a new session.'
            : 'Onboarding was cancelled in the browser. Run the init command again when you are ready.',
        )));
      } else if (message.command || message.bootstrap_code) {
        settle(() => reject(new Error(LEGACY_BOOTSTRAP_RESPONSE_MESSAGE)));
      }
    });

    socket.addEventListener('error', () => {
      settle(() => reject(new PlatformAuthWsTransportError('Platform auth websocket failed')));
    });

    // A close before any terminal message is a transport failure too — the
    // old code left this promise hanging until the session-expiry timeout.
    socket.addEventListener('close', () => {
      settle(() => reject(new PlatformAuthWsTransportError('Platform auth websocket closed before completion')));
    });
  });
}

export function waitForPlatformProject(
  session: PlatformCliStartResponse,
  onProgress?: (progress: PlatformAuthProgress) => void,
): Promise<PlatformProjectPayload> {
  // WebSocket push is primary (AB-06): the platform delivers progress and
  // completion as events. The 1.5s HTTP status loop (~40 requests/minute per
  // waiting CLI) remains only as the bounded failure fallback for a broken
  // WebSocket transport — and the sole path for sessions without a ws_url.
  if (session.ws_url) {
    return waitForPlatformProjectWs(session.ws_url, session.expires_at, { onProgress }).catch((error) => {
      if (error instanceof PlatformAuthWsTransportError && session.status_url && Date.now() < session.expires_at) {
        return waitForPlatformProjectStatus(session.status_url, session.expires_at, { onProgress });
      }
      throw error;
    });
  }
  if (session.status_url) {
    return waitForPlatformProjectStatus(session.status_url, session.expires_at, { onProgress });
  }
  return Promise.reject(new Error('Platform auth session provided no ws_url or status_url'));
}

function upsertEnvLine(lines: string[], key: string, value: string): string[] {
  return [
    ...lines.filter((line) => !line.startsWith(`${key}=`)),
    `${key}=${value}`,
  ];
}

function hasEnvLine(lines: string[], key: string): boolean {
  return lines.some((line) => line.startsWith(`${key}=`));
}

function appendGitignoreEntries(projectPath: string, heading: string, entries: string[]): void {
  const gitignorePath = path.join(projectPath, '.gitignore');
  const gitignoreContent = fs.existsSync(gitignorePath)
    ? fs.readFileSync(gitignorePath, 'utf-8')
    : '';
  const existingEntries = new Set(gitignoreContent.split(/\r?\n/));
  const missingEntries = entries.filter((entry) => !existingEntries.has(entry));
  if (missingEntries.length === 0) return;

  const prefix =
    gitignoreContent.length === 0
      ? ''
      : gitignoreContent.endsWith('\n')
        ? '\n'
        : '\n\n';
  fs.appendFileSync(gitignorePath, `${prefix}${heading}\n${missingEntries.join('\n')}\n`);
}

export function writeStandardAgentsSupportFiles(
  projectPath: string,
  params: {
    projectName: string;
    packageManager: SupportedPackageManager;
  },
): string[] {
  const written: string[] = [];
  // Pin generated @standardagents/* dependencies to the CLI's own published
  // version so `npx @standardagents/cli@next init` produces a project on the
  // matching next-tagged releases instead of drifting back to `latest`.
  for (const supportFile of renderAgentBuilderSupportFiles({
    projectName: params.projectName,
    packageManager: params.packageManager as PackageManager,
    versions: {
      standardAgents: getCliVersion(),
      sip: getSipVersion(),
    },
  })) {
    const targetPath = path.join(projectPath, supportFile.path);
    if (fs.existsSync(targetPath)) continue;
    fs.mkdirSync(path.dirname(targetPath), { recursive: true });
    fs.writeFileSync(targetPath, supportFile.content, 'utf-8');
    written.push(supportFile.path);
  }

  // package.json AND the lockfile are committed: the project owns its
  // @standardagents/* versions and the platform build installs them frozen for
  // reproducible deploys (using whichever package manager the lockfile implies),
  // so neither is git-ignored. Only dependencies and local build/dev state stay
  // out of the repo. Idempotent and ungated: seeded repos already commit a
  // matching .gitignore (so this adds nothing), and legacy/--local repos get
  // backfilled regardless of whether a package.json was just written.
  appendGitignoreEntries(projectPath, '# Standard Agents local dev state', [
    'node_modules',
    '.agents',
    '.wrangler',
    'dist',
    '.agentbuilder-dev.log',
  ]);

  return written;
}

export function supportFiles(options: { projectName?: string; packageManager?: string } = {}): void {
  let packageManager: SupportedPackageManager;
  try {
    packageManager = resolvePackageManager(options.packageManager, detectPackageManager());
  } catch (error) {
    logger.error(error instanceof Error ? error.message : String(error));
    process.exit(1);
  }

  const cwd = process.cwd();
  const projectName = normalizeProjectName(options.projectName ?? path.basename(cwd)) || 'standard-agents';
  const written = writeStandardAgentsSupportFiles(cwd, {
    projectName,
    packageManager,
  });
  if (written.length === 0) {
    logger.info('Standard Agents local support files already exist');
    return;
  }
  logger.success(`Wrote Standard Agents local support files: ${written.join(', ')}`);
}

export function writeStandardAgentsEnv(projectPath: string, params: {
  apiKey: string;
  platformEndpoint: string;
}): string {
  const envPath = path.join(projectPath, '.dev.vars');
  const existing = fs.existsSync(envPath) ? fs.readFileSync(envPath, 'utf-8') : '';
  let lines = existing
    .split(/\r?\n/)
    .filter((line) =>
      line.trim().length > 0 &&
      !line.startsWith('STANDARD_AGENTS_API_KEY=') &&
      !line.startsWith('PLATFORM_ENDPOINT=') &&
      !line.startsWith('STANDARD_AGENTS_API_URL=')
    );

  const needsEncryptionKey = !hasEnvLine(lines, 'ENCRYPTION_KEY');
  const needsAdminPassword = !hasEnvLine(lines, 'SUPER_ADMIN_PASSWORD');
  if (needsEncryptionKey || needsAdminPassword) {
    if (lines.length > 0) {
      lines.push('');
    }
    lines.push('# Local admin access');
    if (needsEncryptionKey) {
      lines.push(`ENCRYPTION_KEY=${crypto.randomBytes(32).toString('hex')}`);
    }
    if (needsAdminPassword) {
      lines.push('SUPER_ADMIN_PASSWORD=password');
    }
  }

  if (lines.length > 0) {
    lines.push('');
  }
  lines.push('# Standard Agents platform connection');
  lines = upsertEnvLine(lines, 'STANDARD_AGENTS_API_KEY', params.apiKey);
  lines = upsertEnvLine(lines, 'PLATFORM_ENDPOINT', params.platformEndpoint);
  lines = upsertEnvLine(lines, 'STANDARD_AGENTS_API_URL', params.platformEndpoint);
  fs.writeFileSync(envPath, lines.join('\n').replace(/\n{3,}/g, '\n\n').trim() + '\n', 'utf-8');

  appendGitignoreEntries(projectPath, '# Standard Agents local platform token', ['.dev.vars']);

  return envPath;
}

// Codes minted outside a project's dashboard carry no project. Fall back to
// the first instance the signed-in account can see.
async function findFirstProjectId(
  endpoint: string,
  cookie: string,
  fetchImpl: typeof fetch,
): Promise<string> {
  const listResponse = await fetchImpl(`${endpoint}/projects`, {
    headers: { Accept: 'application/json', Cookie: cookie },
  });
  if (!listResponse.ok) {
    throw new Error(`Could not load your Standard Agents instance (${listResponse.status}).`);
  }
  const listBody = (await listResponse.json().catch(() => ({}))) as {
    projects?: Array<{ id?: unknown }>;
  };
  const projectId = listBody.projects?.find(
    (entry): entry is { id: string } => typeof entry?.id === 'string',
  )?.id;
  if (!projectId) {
    throw new Error('No Standard Agents instance found for this account. Create one in the dashboard first.');
  }
  return projectId;
}

// Bootstrap path: the project was already created in the browser, so exchange
// the one-time code for a session, use the code's project (or the account's
// first instance for codes minted outside a project), and ask the
// platform to re-issue clone credentials (fresh local API key + short-lived
// git token). Returns the same payload shape the browser poll yields, so the
// downstream clone/install/dev path is identical.
export async function resolveBootstrapProject(
  endpoint: string,
  code: string,
  fetchImpl: typeof fetch = fetch,
): Promise<PlatformProjectPayload> {
  const consumed = await exchangeBootstrapCode(endpoint, code, fetchImpl);
  const cookie = consumed.session_cookie;
  const projectId = consumed.project?.id ?? await findFirstProjectId(endpoint, cookie, fetchImpl);

  const handoffResponse = await fetchImpl(`${endpoint}/projects/${encodeURIComponent(projectId)}/cli-handoff`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', Cookie: cookie },
    body: '{}',
  });
  if (!handoffResponse.ok) {
    const text = await handoffResponse.text().catch(() => '');
    throw new Error(`Could not prepare your instance for local setup (${handoffResponse.status})${text ? `: ${text}` : ''}`);
  }
  const handoffBody = (await handoffResponse.json().catch(() => ({}))) as { project?: PlatformProjectPayload };
  if (!handoffBody.project?.repo_clone_url || !handoffBody.project.api_key) {
    throw new Error('Instance handoff response was missing clone details.');
  }
  return handoffBody.project;
}

async function runPlatformInit(projectNameArg: string | undefined, options: InitOptions): Promise<void> {
  const cwd = process.cwd();

  let pm: SupportedPackageManager;
  try {
    pm = resolvePackageManager(options.packageManager, detectPackageManager());
  } catch (error) {
    logger.error(error instanceof Error ? error.message : String(error));
    process.exit(1);
  }

  const requestedProjectName = projectNameArg?.trim() || undefined;
  if (requestedProjectName && fs.existsSync(path.join(cwd, requestedProjectName))) {
    logger.error(`Directory "${requestedProjectName}" already exists`);
    process.exit(1);
  }

  const endpoint = normalizeApiUrl(options.apiUrl || options.endpoint);

  // A bootstrap code means the instance already exists (created in the browser),
  // so clone it directly. Otherwise run the interactive browser onboarding.
  let project: PlatformProjectPayload;
  if (options.bootstrap?.trim()) {
    logger.info('Connecting to your Standard Agents instance...');
    try {
      project = await resolveBootstrapProject(endpoint, options.bootstrap.trim());
    } catch (error) {
      logger.error(error instanceof Error ? error.message : String(error));
      process.exit(1);
    }
  } else {
    logger.info('Opening Standard Agents platform signup/login...');
    let session: PlatformCliStartResponse;
    try {
      session = await startPlatformCliSession({
        endpoint,
        projectName: requestedProjectName,
        packageManager: pm,
      });
    } catch (error) {
      logger.error(error instanceof Error ? error.message : String(error));
      process.exit(1);
    }

    openUrl(session.auth_url);
    logger.info('Finish the browser steps. I will stay here and continue automatically.');

    project = await waitForPlatformProject(session, (progress) => {
      logger.info(progress.message);
    });
  }
  const projectName = resolvePlatformProjectDirectoryName(project, requestedProjectName);
  const projectPackageManager = resolvePlatformProjectPackageManager(project.package_manager, pm);
  const projectPath = path.join(cwd, projectName);
  if (fs.existsSync(projectPath)) {
    logger.error(`Directory "${projectName}" already exists`);
    process.exit(1);
  }

  logger.success('Browser setup complete.');
  logger.info(`Cloning ${cloneDisplayUrl(project)}...`);
  await runCommand('git', gitCloneArgs(project, projectName), cwd);
  if (project.git_extra_header && project.repo_html_url) {
    await runCommand('git', ['remote', 'set-url', 'origin', project.repo_html_url], projectPath);
  }

  const supportFiles = writeStandardAgentsSupportFiles(projectPath, {
    projectName: project.name || projectName,
    packageManager: projectPackageManager,
  });
  if (supportFiles.length > 0) {
    logger.success(`Wrote generated local support files: ${supportFiles.join(', ')}.`);
  }

  const envPath = writeStandardAgentsEnv(projectPath, {
    apiKey: project.api_key,
    platformEndpoint: endpoint,
  });
  logger.success(`Wrote Standard Agents credentials to ${path.relative(cwd, envPath)}.`);
  if (project.git_extra_header) {
    await configurePlatformGitCredentialHelper(projectPath);
    logger.success('Configured git credentials for the platform repository.');
  }

  const linkedPackages = preparePlatformProjectDependencies(projectPath, projectPackageManager);
  if (linkedPackages.length > 0) {
    logger.info(`Workspace mode detected: linked ${linkedPackages.length} Standard Agents packages locally.`);
  }

  logger.info(`Installing dependencies with ${projectPackageManager}...`);
  await runCommand(projectPackageManager, ['install'], projectPath);

  const repoUrl = project.repo_html_url ?? project.repo_clone_url;
  const tokenLocation = path.relative(cwd, envPath);

  // --no-dev: clone + configure only (handy for CI/scripted setup).
  if (options.dev === false) {
    logger.success('Your Standard Agents instance is ready.');
    logger.log(`Repository:  ${repoUrl}`);
    logger.log(`Local token: ${tokenLocation}`);
    logger.log('');
    logger.log('Start it whenever you like:');
    logger.log(`  cd ${projectName}`);
    logger.log(`  ${devCommandHint(projectPackageManager)}`);
    return;
  }

  const devPort = await findAvailablePort(5173);
  const devUrl = `http://127.0.0.1:${devPort}`;
  logger.info(`Starting local dev server at ${devUrl}...`);
  logger.info('The dev server will stay in this terminal. Press Ctrl-C to stop it.');
  const devProcess = startForegroundDevServer(
    projectPackageManager,
    buildDevServerCommand(projectPackageManager, devPort),
    projectPath,
  );
  const devExit = waitForDevServerExit(devProcess);
  let startup:
    | { type: 'ready'; ready: boolean }
    | { type: 'exit'; code: number | null };

  try {
    startup = await Promise.race([
      waitForLocalDevServer(devUrl).then((ready) => ({ type: 'ready' as const, ready })),
      devExit.then((code) => ({ type: 'exit' as const, code })),
    ]);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    logger.warning(`The dev server could not start: ${message}`);
    logger.log('');
    logger.success('Your Standard Agents instance is cloned, configured, and ready.');
    logger.log(`Repository:  ${repoUrl}`);
    logger.log(`Local token: ${tokenLocation}`);
    logger.log('');
    logger.log('Once any error above is resolved, start it with:');
    logger.log(`  cd ${projectName}`);
    logger.log(`  ${devCommandHint(projectPackageManager)}`);
    return;
  }

  if (startup.type === 'ready' && startup.ready) {
    openUrl(devUrl);
    logger.success('Project is connected to Standard Agents.');
    logger.log(`Repository:  ${repoUrl}`);
    logger.log(`Dev server:  ${devUrl}`);
    logger.log(`Local token: ${tokenLocation}`);
    logger.log('');
    logger.log('The dev server is running in the foreground. Press Ctrl-C to stop it.');
    const exitCode = await devExit;
    if (exitCode !== null && exitCode !== 0) {
      logger.warning(`Dev server exited with code ${exitCode}.`);
    }
    return;
  }

  if (startup.type === 'exit') {
    logger.warning(`The dev server exited before it responded at ${devUrl}.`);
    logger.log('');
    logger.success('Your Standard Agents instance is cloned, configured, and ready.');
    logger.log(`Repository:  ${repoUrl}`);
    logger.log(`Local token: ${tokenLocation}`);
    logger.log('');
    logger.log('Once any error above is resolved, start it with:');
    logger.log(`  cd ${projectName}`);
    logger.log(`  ${devCommandHint(projectPackageManager)}`);
    return;
  }

  // Clone + install + config all succeeded; only the dev server didn't respond
  // in time. Keep it attached so the user can inspect output and stop it.
  logger.warning(`The dev server didn't respond at ${devUrl} within 60s.`);
  logger.log('');
  logger.success('Your Standard Agents instance is cloned, configured, and ready.');
  logger.log(`Repository:  ${repoUrl}`);
  logger.log(`Dev server:  ${devUrl}`);
  logger.log(`Local token: ${tokenLocation}`);
  logger.log('');
  logger.log('The dev server process is still running in this terminal. Press Ctrl-C to stop it.');
  const exitCode = await devExit;
  if (exitCode !== null && exitCode !== 0) {
    logger.warning(`Dev server exited with code ${exitCode}.`);
  }
}

export async function init(projectNameArg?: string, options: InitOptions = {}) {
  if (!options.local) {
    await runPlatformInit(projectNameArg, options);
    return;
  }

  const cwd = process.cwd();
  const detectedPm = detectPackageManager();
  const template = options.template || 'vanilla-ts';
  const initTuiMode = isInitTuiEnabled(options);
  if (initTuiMode) {
    clearTerminalScreen();
  }
  const logInfo = (message: string) => {
    if (!initTuiMode) logger.info(message);
  };
  const logWarning = (message: string) => {
    if (!initTuiMode) logger.warning(message);
  };
  const logSuccess = (message: string) => {
    if (!initTuiMode) logger.success(message);
  };
  const logLine = (message = '') => {
    if (!initTuiMode) logger.log(message);
  };

  let projectName = '';
  let projectPath = '';
  let existingBootstrapState: BootstrapSessionState | null = null;
  let resumeBootstrap = false;
  let useExistingProject = false;
  const headerOptions: InitStepHeaderOptions = { tuiMode: initTuiMode };
  const promptProjectName = async (question: string): Promise<string> => {
    if (!initTuiMode) {
      return prompt(question);
    }
    clearTerminalScreen();
    const value = await promptProjectNameWithInk(question, {
      stage: 'project',
      title: 'Creating project scaffold',
      subtitle: 'Choose the directory name for your new project.',
      projectName: headerOptions.projectName,
      packageManager: headerOptions.packageManager,
      authMode: headerOptions.authMode,
    });
    if (value === null) {
      throw new InitWizardReturnToHomeError();
    }
    return value;
  };
  try {
    const target = await resolveProjectTarget(cwd, projectNameArg, options, promptProjectName);
    projectName = target.projectName;
    projectPath = target.projectPath;
    existingBootstrapState = target.existingBootstrapState;
    resumeBootstrap = target.resumeBootstrap;
    useExistingProject = target.useExistingProject;
    headerOptions.projectName = projectName;
  } catch (error) {
    if (isInitWizardReturnToHomeError(error)) {
      exitOrThrowReturnToHome(options);
    }
    logger.error(error instanceof Error ? error.message : 'Project name is required');
    process.exit(1);
  }

  renderInitStepHeader(
    'project',
    useExistingProject ? 'Inspecting existing project' : 'Creating project scaffold',
    useExistingProject
      ? `Using "${projectName}" in the current workspace.`
      : `Preparing "${projectName}" with template "${template}".`,
    headerOptions,
  );

  if (useExistingProject) {
    logInfo(`Using existing Standard Agents project "${projectName}"...`);
  } else {
    logInfo('Creating a new Standard Agents project...');
  }

  // Select package manager
  let pm: SupportedPackageManager;
  if (options.packageManager) {
    try {
      pm = resolvePackageManager(options.packageManager, detectedPm);
    } catch (error) {
      logger.error(error instanceof Error ? error.message : String(error));
      process.exit(1);
    }
  } else if (useExistingProject || options.yes) {
    pm = detectedPm;
  } else {
    try {
      pm = await selectPackageManager(detectedPm, {
        projectName: headerOptions.projectName,
        authMode: headerOptions.authMode,
      });
    } catch (error) {
      if (isInitWizardReturnToHomeError(error)) {
        exitOrThrowReturnToHome(options);
      }
      throw error;
    }
    logLine('');
  }

  headerOptions.packageManager = pm;
  logInfo(`Using package manager: ${pm}`);

  let packageSpecifier = '';
  try {
    packageSpecifier = resolvePackageSpecifier(options, pm);
  } catch (error) {
    logger.error(error instanceof Error ? error.message : 'Invalid package install mode');
    process.exit(1);
  }

  const providedBootstrap = options.bootstrap?.trim() ?? '';
  if (providedBootstrap) {
    logInfo('Bootstrap code supplied.');
  } else if (existingBootstrapState) {
    logInfo('Found existing StandardAgents auth session for this project.');
  } else {
    logInfo('No StandardAgents CLI auth session found.');
  }
  logLine('');

  const willShowAuthCardPrompt =
    initTuiMode &&
    !existingBootstrapState &&
    !providedBootstrap &&
    !options.yes;
  if (!willShowAuthCardPrompt) {
    renderInitStepHeader(
      'auth',
      'Account linking',
      existingBootstrapState
        ? 'Reusing saved project auth session if compatible with endpoint.'
        : 'Choose StandardAgents or Local mode.',
      headerOptions,
    );
  }

  let authResult: EnsureAuthResult = { connected: false, mode: 'local' };
  if (!existingBootstrapState) {
    try {
      authResult = await ensurePlatformAuth(options, initTuiMode, {
        projectName: headerOptions.projectName,
        packageManager: headerOptions.packageManager,
      });
    } catch (error) {
      if (isInitWizardReturnToHomeError(error)) {
        exitOrThrowReturnToHome(options);
      }
      throw error;
    }
  } else {
    authResult = { connected: true, mode: 'standardagents' };
  }
  headerOptions.authMode = authResult.mode;

  const bootstrapRequested = Boolean(options.bootstrap?.trim()) || existingBootstrapState !== null;
  if (!bootstrapRequested && authResult.mode === 'local') {
    logInfo('Continuing in Local mode (no org link yet).');
  }

  let bootstrapSession: BootstrapSessionState | null = null;
  const isManagedOrgMode = authResult.mode === 'standardagents';

  if (!isManagedOrgMode) {
    renderInitStepHeader(
      'env',
      'Environment setup',
      'Collecting local credentials and writing .dev.vars.',
      headerOptions,
    );
  }

  // Generate encryption key
  const encryptionKey = crypto.randomBytes(32).toString('hex');

  // Ask for API keys and admin password
  let openrouterKey = '';
  let openaiKey = '';
  let adminPassword = '';

  if (isManagedOrgMode) {
    logInfo('Managed Standard Agents auth detected. Skipping local provider/admin prompts.');
  } else if (!options.yes) {
    if (initTuiMode) {
      const localEnvInput = await collectLocalEnvWithInk({
        projectName: headerOptions.projectName,
        packageManager: headerOptions.packageManager,
        authMode: headerOptions.authMode,
      });
      if (!localEnvInput) {
        exitOrThrowReturnToHome(options);
      }
      openrouterKey = localEnvInput.openrouterKey;
      openaiKey = localEnvInput.openaiKey;
      adminPassword = localEnvInput.adminPassword;
    } else {
      logLine('API keys are optional but required to use AI models.');
      logLine('You can add them later by editing .dev.vars');
      logLine('');

      openrouterKey = await prompt('OpenRouter API key (optional, press Enter to skip): ');
      openaiKey = await prompt('OpenAI API key (optional, press Enter to skip): ');

      logLine('');
      logLine('Set a temporary admin password for development access.');
      logLine('Press Enter to use the default password "admin".');
      logLine('');

      while (true) {
        adminPassword = await promptPassword('Temporary admin password: ');

        if (!adminPassword) {
          adminPassword = 'admin';
          break;
        }

        const confirmPassword = await promptPassword('Confirm password: ');

        if (adminPassword === confirmPassword) {
          break;
        } else {
          logWarning('Passwords do not match. Please try again.');
          logLine('');
          adminPassword = '';
        }
      }
    }
  }

  // Default admin password if not provided (for --yes flag)
  if (!isManagedOrgMode && !adminPassword) {
    adminPassword = 'admin';
  }

  renderInitStepHeader(
    'scaffold',
    'Scaffold project',
    'Installing dependencies and applying Standard Agents project structure.',
    headerOptions,
  );

  if (!useExistingProject) {
    logInfo(`Running create-vite (${template})...`);

    try {
      await withTuiSpinner(initTuiMode, 'Creating Vite project...', async () => {
        // Determine the correct create command based on package manager
        // All package managers should use create-vite@latest to ensure the latest version
        // Use --no-interactive to skip prompts (like "Install with npm and start now?")
        let createCmd: string;
        let createArgs: string[];

        switch (pm) {
          case 'pnpm':
            createCmd = 'pnpm';
            createArgs = ['create', 'vite@latest', projectName, '--template', template, '--no-interactive'];
            break;
          case 'yarn':
            createCmd = 'yarn';
            createArgs = ['create', 'vite@latest', projectName, '--template', template, '--no-interactive'];
            break;
          case 'bun':
            createCmd = 'bun';
            createArgs = ['create', 'vite@latest', projectName, '--template', template, '--no-interactive'];
            break;
          default:
            createCmd = 'npm';
            createArgs = ['create', 'vite@latest', projectName, '--', '--template', template, '--no-interactive'];
        }

        await runCommand(createCmd, createArgs, cwd, { quiet: initTuiMode });
      });
    } catch (error) {
      logger.error(
        error instanceof Error
          ? `Failed to create Vite project: ${error.message}`
          : 'Failed to create Vite project',
      );
      process.exit(1);
    }

    // Create vite.config.ts if it doesn't exist (some templates may not include one)
    const viteConfigPath = path.join(projectPath, 'vite.config.ts');
    if (!fs.existsSync(viteConfigPath)) {
      const viteConfigContent = `import { defineConfig } from 'vite'

export default defineConfig({
  plugins: [],
})
`;
      fs.writeFileSync(viteConfigPath, viteConfigContent, 'utf-8');
      logSuccess('Created vite.config.ts');
    }

  } else if (resumeBootstrap) {
    logInfo(`Resuming bootstrap-authenticated init in existing directory "${projectName}"...`);
  } else {
    logInfo(`Detected existing Standard Agents project in "${projectName}".`);
  }

  // Update package.json name to kebab-case (handles spaces and special characters)
  // and inject local-only helper scripts when workspace mode is active.
  const packageJsonPath = path.join(projectPath, 'package.json');
  if (fs.existsSync(packageJsonPath)) {
    const packageJson = JSON.parse(fs.readFileSync(packageJsonPath, 'utf-8'));
    const kebabName = toKebabCase(projectName);
    if (packageJson.name !== kebabName) {
      packageJson.name = kebabName;
      fs.writeFileSync(packageJsonPath, JSON.stringify(packageJson, null, 2) + '\n', 'utf-8');
    }

    if (packageSpecifier === 'workspace:*' && configureWorkspaceProjectScripts(projectPath, projectName)) {
      logInfo('Configured local workspace helper scripts for sandbox HMR and re-auth.');
    }
  }

  if (bootstrapRequested) {
    logInfo('Exchanging bootstrap code and persisting local project auth...');

    try {
      const prepared = await prepareBootstrapSession(projectPath, options);
      if (!prepared) {
        logger.error('No resumable bootstrap session found. Provide --bootstrap <code>.');
        process.exit(1);
      }
      bootstrapSession = prepared.state;
      if (prepared.resumed) {
        logSuccess('Reused existing authenticated bootstrap session');
      } else {
        logSuccess('Bootstrap code exchanged and local auth session persisted');
      }
    } catch (error) {
      logger.error(error instanceof Error ? error.message : 'Bootstrap exchange failed');
      process.exit(1);
    }
  }

  try {
    const installCmd = pm === 'npm' ? 'install' : 'add';

    if (packageSpecifier === 'workspace:*') {
      logInfo('Using local workspace @standardagents/* packages (workspace:*).');
    }

    await withTuiSpinner(initTuiMode, 'Installing dependencies...', async () => {
      // Build list of regular dependencies
      // All @standardagents packages are version-locked to CLI version
      // We explicitly install all packages at the exact version to avoid
      // npm/pnpm resolving transitive deps with semver ranges (which excludes prereleases)
      const standardAgentsPackage = (packageName: string) =>
        `${packageName}@${resolveStandardAgentsPackageSpecifier(packageName, packageSpecifier)}`;
      const deps = [
        standardAgentsPackage('@standardagents/builder'),
        standardAgentsPackage('@standardagents/spec'),
        standardAgentsPackage('@standardagents/sip'),
        'zod'
      ];

      // Add provider packages based on API keys provided
      if (openaiKey) {
        deps.push(standardAgentsPackage('@standardagents/openai'));
      }
      if (openrouterKey) {
        deps.push(standardAgentsPackage('@standardagents/openrouter'));
      }

      // Install regular dependencies (builder needs to be in dependencies, not devDependencies)
      await runCommand(pm, [installCmd, ...deps], projectPath, { quiet: initTuiMode });
    });
  } catch (error) {
    logger.error(
      error instanceof Error
        ? `Failed to install dependencies: ${error.message}`
        : 'Failed to install dependencies',
    );
    process.exit(1);
  }

  // Change to project directory for scaffold
  process.chdir(projectPath);

  try {
    const restoreLogger = silenceLoggerOutput(initTuiMode);
    try {
      await withTuiSpinner(initTuiMode, 'Scaffolding project files...', async () => {
        await scaffold({ force: true });
      });
    } finally {
      restoreLogger();
    }
  } catch (error) {
    if (bootstrapSession) {
      logWarning(
        `Scaffold failed after bootstrap authentication. Resume by rerunning: npx @standardagents/cli init ${projectName}`,
      );
    }
    logger.error(
      error instanceof Error
        ? `Failed to scaffold Standard Agents project: ${error.message}`
        : 'Failed to scaffold Standard Agents project',
    );
    process.exit(1);
  }

  // Write .dev.vars file with collected environment variables
  logInfo('Writing environment configuration...');

  // Create .dev.vars file
  const devVarsLines: string[] = [
    '# Standard Agents Environment Variables',
    '# This file contains secrets for local development only',
    '# Do not commit this file to version control',
    '',
    '# Encryption key for secure data (auto-generated)',
    `ENCRYPTION_KEY=${encryptionKey}`,
  ];

  const resolvedPlatformEndpoint = resolvePlatformEndpoint(options);
  if (isManagedOrgMode || options.endpoint || options.apiUrl || process.env.PLATFORM_ENDPOINT) {
    devVarsLines.push(
      '',
      '# Platform API endpoint for account, billing, and deploy integration',
      `PLATFORM_ENDPOINT=${resolvedPlatformEndpoint}`,
    );
  }

  if (!isManagedOrgMode) {
    devVarsLines.push(
      '',
      '# Temporary admin password for development access',
      `SUPER_ADMIN_PASSWORD=${adminPassword}`,
    );
  }

  if (bootstrapSession) {
    devVarsLines.push(
      '',
      '# Bootstrap-authenticated org context',
      `STANDARDAGENTS_ORG_ACCOUNT_ID=${bootstrapSession.account.id}`,
      `STANDARDAGENTS_ORG_USER_ID=${bootstrapSession.user.id}`,
      `STANDARDAGENTS_BOOTSTRAP_SESSION_COOKIE=${bootstrapSession.session_cookie}`,
    );
    if (bootstrapSession.account.slug) {
      devVarsLines.push(`STANDARDAGENTS_ORG_SLUG=${bootstrapSession.account.slug}`);
    }
  }

  if (isManagedOrgMode) {
    devVarsLines.push(
      '',
      '# Managed org mode',
      '# Model access, proxy routing, and credit are handled by your Standard Agents account.',
      '# Optional local overrides:',
      '# OPENROUTER_API_KEY=your-openrouter-key',
      '# OPENAI_API_KEY=your-openai-key',
    );
  } else {
    devVarsLines.push(
      '',
      '# AI Provider API Keys',
      '# Uncomment and add your keys as needed',
    );

    if (openrouterKey) {
      devVarsLines.push(`OPENROUTER_API_KEY=${openrouterKey}`);
    } else {
      devVarsLines.push('# OPENROUTER_API_KEY=your-openrouter-key');
    }

    if (openaiKey) {
      devVarsLines.push(`OPENAI_API_KEY=${openaiKey}`);
    } else {
      devVarsLines.push('# OPENAI_API_KEY=your-openai-key');
    }
  }

  devVarsLines.push('');

  const devVarsPath = path.join(projectPath, '.dev.vars');
  fs.writeFileSync(devVarsPath, devVarsLines.join('\n'), 'utf-8');
  logSuccess('Created .dev.vars with encryption key');

  // Add .dev.vars to .gitignore if it exists
  const gitignorePath = path.join(projectPath, '.gitignore');
  if (fs.existsSync(gitignorePath)) {
    const gitignoreContent = fs.readFileSync(gitignorePath, 'utf-8');
    if (!gitignoreContent.includes('.dev.vars')) {
      fs.appendFileSync(gitignorePath, '\n# Local environment variables\n.dev.vars\n');
      logSuccess('Added .dev.vars to .gitignore');
    }
  }

  // Done!
  const nextRunCommand = resolvePostInitRunCommand(pm, projectPath, packageSpecifier === 'workspace:*');
  if (initTuiMode) {
    clearTerminalScreen();
    process.stdout.write(
      renderInitCompleteFrame({
        useExistingProject,
        projectName,
        nextCommand: nextRunCommand,
        bootstrapSession: Boolean(bootstrapSession),
      }),
    );
    return;
  }

  logger.log('');
  logger.success(useExistingProject ? 'Project updated successfully!' : 'Project created successfully!');
  logger.log('');
  logger.log('Next steps:');
  logger.log('');
  if (!useExistingProject) {
    logger.log(`  cd ${projectName}`);
  }
  logger.log(`  ${nextRunCommand}`);
  logger.log('');
  if (!bootstrapSession) {
    logger.log('Local-only mode notes:');
    logger.log('  1. Sign in locally with username `admin` and your temporary `SUPER_ADMIN_PASSWORD`.');
    logger.log('  2. Create a standard user account for yourself.');
    logger.log('  3. Remove `SUPER_ADMIN_PASSWORD` from `.dev.vars` once setup is complete.');
    logger.log('');
  }
  logger.log('For more information, visit: https://standardagents.ai/docs');
}
